import { useTranslation } from "@redotech/redo-i18n";
import { PriceOption } from "@redotech/redo-return-core-common/condition-options";
import type { CompareAtPriceState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { getPriceOptionLabel } from "./price-option-labels";

export const CompareAtPriceDetails = memo(function CompareAtPriceDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<CompareAtPriceState>) {
  const { t } = useTranslation();
  const comparisonOptions: RedoListItem<PriceOption>[] = useMemo(
    () =>
      Object.values(PriceOption)
        .filter((option) => option !== PriceOption.CENTS_EQUALS)
        .map((option) => ({
          id: option,
          value: option,
          type: "text" as const,
          text: getPriceOptionLabel(option, t),
        })),
    [t],
  );

  const selectedOption = state.priceOption
    ? comparisonOptions.find((option) => option.value === state.priceOption)
    : undefined;

  const comparisonText =
    state.priceOption === PriceOption.LESS_THAN
      ? t("Less than").toLowerCase()
      : state.priceOption === PriceOption.GREATER_THAN
        ? t("Greater than").toLowerCase()
        : state.priceOption === PriceOption.EQUALS
          ? t("Equals").toLowerCase()
          : "[comparison]";
  const percentText =
    state.percentage === "" ? "[percentage]%" : `${state.percentage}%`;

  return (
    <Flex dir="column" gap="sm">
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={comparisonOptions}
        optionSelected={({ item }) =>
          setState({ ...state, priceOption: item.value })
        }
        placeholder={t("Select comparison...")}
        selectedItem={selectedOption}
        size="xs"
      />
      <RedoTextInput
        IconTrailing={() => <>%</>}
        inputTypeHint="number"
        label={t("Percentage of compare-at price")}
        max={100}
        min={0}
        onBlur={() => {
          if (state.percentage === "") {
            return;
          }
          const clamped = Math.min(
            100,
            Math.max(0, Number(state.percentage) || 0),
          );
          setState({ ...state, percentage: String(clamped) });
        }}
        placeholder="50"
        setValue={(percentage) => setState({ ...state, percentage })}
        state={disabled ? "readonly" : "default"}
        value={state.percentage}
      />
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        {t(
          "Matches when the item's price is {{comparison}} {{percent}} of its compare-at price.",
          { comparison: comparisonText, percent: percentText },
        )}
      </Text>
      <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
        {t(
          "Compare-at price is measured at the time of order. Items with no compare-at price will evaluate to false.",
        )}
      </Text>
    </Flex>
  );
});
